import {differenceInDays, format, parse, parseISO} from 'date-fns'

import {formatFixedNumber, formatHectares} from '../../../../../shared/numberFormat'
import {
  PDF_PAGE,
  createPainter,
  drawFooter,
  drawHeaderBand,
  drawMetricChips,
  drawSectionHeader,
  escapePdfText,
  estimateTextWidth,
  getPdfPalette,
  hexToRgb,
  wrapText,
} from '../../../../../shared/pdf/pdfTheme'
import {getDataUrlImageHex, loadTenantLogoImage} from '../../../../../shared/pdf/pdfTenantLogo'
import {buildFieldLandDownloadSubject} from '../../../Fields/ViewField/ViewLand/Layers/downloadArchive'
import {getNdviReportSourceLabel} from '../ndviReportSource'

const {width: PAGE_WIDTH, height: PAGE_HEIGHT, marginX: MARGIN_X, marginBottom: MARGIN_BOTTOM} = PDF_PAGE
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2
const CHART_HEIGHT = 160
const ROW_HEIGHT = 18
const TABLE_HEADER_HEIGHT = 22
const TABLE_TITLE_HEIGHT = 24
const TABLE_BOTTOM_PADDING = 12
const DATE_AXIS_LABEL_SIZE = 6.4
const CLASS_COLORS = ['#f1416c', '#ffc700', '#7239ea', '#50cd89', '#1b84ff', '#8950fc']
const LAND_COLORS = [
  '#1b84ff',
  '#50cd89',
  '#f1416c',
  '#ffc700',
  '#7239ea',
  '#00a3ff',
  '#1bc5bd',
  '#8950fc',
  '#6993ff',
  '#e49e3d',
]

const parseReportDate = (value) => {
  if (!value) {
    return null
  }

  try {
    if (String(value).includes('T')) {
      return parseISO(value)
    }
    return parse(String(value).slice(0, 10), 'yyyy-MM-dd', new Date())
  } catch (error) {
    return null
  }
}

const formatReportDate = (value) => {
  const date = value instanceof Date ? value : parseReportDate(value)
  return date ? format(date, 'dd/MM/yyyy') : '-'
}

const formatRankingVariation = (value) => {
  const numericValue = Number(value || 0)
  return numericValue > 0 ? `+${numericValue}` : String(numericValue)
}

const getReportDateKey = (value) => {
  const date = value instanceof Date ? value : parseReportDate(value)
  return date ? format(date, 'yyyy-MM-dd') : ''
}

const getLandId = (land) => land?.land_id || land?.id

const getLandName = (land) =>
  buildFieldLandDownloadSubject({
    fieldName: land?.field?.name || land?.field_name,
    landName: land?.name,
  }) || land?.name || '-'

const sanitizeFileNameCharacters = (value) => {
  const invalidCharacters = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*'])

  return Array.from(String(value || ''))
    .map((character) => (
      invalidCharacters.has(character) || character.charCodeAt(0) < 32
        ? '-'
        : character
    ))
    .join('')
}

const normalizeNdviReportFileName = (value) => {
  const name = String(value || '').trim()

  if (!name) {
    return name
  }

  return name
    .replace(/^Reporte\s+NDVI\b/i, 'Reporte de estado de cultivo')
    .replace(/^NDVI\s+Report\b/i, 'Crop status report')
}

const createSafeFileName = (reportName, fallbackName) => {
  const base = sanitizeFileNameCharacters(normalizeNdviReportFileName(reportName || fallbackName))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim()

  return `${base || fallbackName}.pdf`
}

const rgbOps = (color) => hexToRgb(color).map((value) => value.toFixed(3)).join(' ')

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value))

const getChartDateLabelItems = ({dates = [], getPointX, maxLabelX, plotX, plotW}) => {
  if (!Array.isArray(dates) || dates.length < 1) {
    return []
  }

  const minGap = 5
  const labelLimitX = maxLabelX ?? plotX + plotW
  const labelItems = dates
    .map((date, index) => {
      const tickX = getPointX(index)
      const dateLabel = formatReportDate(date)
      const labelWidth = estimateTextWidth(dateLabel, DATE_AXIS_LABEL_SIZE)
      const projectedLabelWidth = labelWidth * 0.72

      return {
        dateLabel,
        endX: tickX + projectedLabelWidth,
        index,
        labelX: tickX,
        startX: tickX,
      }
    })
    .filter((item) => Number.isFinite(item.labelX) && item.labelX >= plotX - 0.1 && item.endX <= labelLimitX + 0.1)

  const selected = []

  labelItems.forEach((item) => {
    const previous = selected[selected.length - 1]

    if (!previous || item.startX >= previous.endX + minGap) {
      selected.push(item)
    }
  })

  const lastItem = labelItems[labelItems.length - 1]
  if (lastItem && !selected.some((item) => item.index === lastItem.index)) {
    while (selected.length > 0) {
      const previous = selected[selected.length - 1]

      if (lastItem.startX >= previous.endX + minGap) {
        break
      }

      selected.pop()
    }

    selected.push(lastItem)
  }

  return selected.sort((left, right) => left.index - right.index)
}

const createEmptyPdfData = () => ({
  aggregateDates: [],
  aggregateDistributionEvolution: null,
  fieldsCount: 0,
  historyDates: [],
  historySeries: [],
  includedLands: [],
  landDistributionEvolution: [],
  lands: [],
  ranking: [],
  rankingFinalDate: null,
  rankingInitialDate: null,
  totalHa: 0,
})

const getClosestUsedReport = (reports, targetDate) => {
  let closest = null
  let closestDiff = Number.MAX_VALUE

  reports.forEach((item) => {
    const date = parseReportDate(item.date)

    if (!date) {
      return
    }

    const diff = Math.abs(differenceInDays(targetDate, date))
    if (diff < closestDiff) {
      closestDiff = diff
      closest = item
    }
  })

  return closest
}

const getClassRanges = (intervals = []) => {
  if (!Array.isArray(intervals) || intervals.length < 1) {
    return []
  }

  if (typeof intervals[0] === 'number') {
    return intervals.map((start, index) => {
      const end = intervals[index + 1]
      return end == null
        ? `> ${formatFixedNumber(start, 3)}`
        : `${formatFixedNumber(start, 3)} - ${formatFixedNumber(end, 3)}`
    })
  }

  return intervals.map((item, index) => {
    const value = item.interval ?? item.value ?? item.cluster ?? index + 1
    const next = intervals[index + 1]?.interval

    return next == null
      ? `> ${formatFixedNumber(value, 3)}`
      : `${formatFixedNumber(value, 3)} - ${formatFixedNumber(next, 3)}`
  })
}

const buildDistributionFromAggregate = (dateData, landsById) => {
  const intervals = Array.isArray(dateData?.intervals) ? dateData.intervals : []
  const ranges = getClassRanges(intervals)
  const valuesByInterval = intervals.reduce((accumulator, interval) => {
    accumulator[String(interval)] = 0
    return accumulator
  }, {})
  const landUsed = []

  ;(dateData?.lands || []).forEach((item) => {
    const land = landsById.get(String(item.land_id))

    if (land) {
      landUsed.push({
        imageDate: formatReportDate(item.image_date),
        name: getLandName(land),
      })
    }

    ;(item.interval || []).forEach((interval, index) => {
      const intervalKey = String(interval.interval)
      const fallbackKey = String(intervals[index])
      const targetKey = Object.prototype.hasOwnProperty.call(valuesByInterval, intervalKey)
        ? intervalKey
        : fallbackKey

      valuesByInterval[targetKey] = (valuesByInterval[targetKey] || 0) + Number(interval.ha || 0)
    })
  })

  return {
    date: formatReportDate(dateData?.day),
    dateKey: getReportDateKey(dateData?.day),
    landUsed,
    rows: intervals.map((interval, index) => ({
      color: CLASS_COLORS[index % CLASS_COLORS.length],
      label: ranges[index] || `Clase ${index + 1}`,
      value: valuesByInterval[String(interval)] || 0,
    })),
  }
}

const buildRanking = ({lands, reportsByLand, firstDate, secondDate}) => {
  if (!firstDate || !secondDate) {
    return []
  }

  const firstRanking = []
  const secondRanking = []

  lands.forEach((land) => {
    const usedReports = (reportsByLand.get(String(getLandId(land))) || []).filter((item) => item.usage === 'USED')
    const first = getClosestUsedReport(usedReports, firstDate)
    const second = getClosestUsedReport(usedReports, secondDate)

    if (first) {
      firstRanking.push({land, report: first})
    }

    if (second) {
      secondRanking.push({land, report: second})
    }
  })

  firstRanking.sort((left, right) => Number(right.report.avg_index || 0) - Number(left.report.avg_index || 0))
  secondRanking.sort((left, right) => Number(right.report.avg_index || 0) - Number(left.report.avg_index || 0))

  const secondPositions = new Map(secondRanking.map((item, index) => [String(getLandId(item.land)), index + 1]))

  return firstRanking.map((item, index) => {
    const landId = String(getLandId(item.land))
    const secondReport = secondRanking.find((candidate) => String(getLandId(candidate.land)) === landId)?.report
    const initialPosition = index + 1
    const finalPosition = secondPositions.get(landId) || initialPosition

    return {
      firstAvgIndex: Number(item.report.avg_index || 0),
      finalPosition,
      initialPosition,
      land: item.land,
      position: initialPosition,
      secondAvgIndex: Number(secondReport?.avg_index || 0),
      secondPosition: finalPosition,
      variation: initialPosition - finalPosition,
    }
  })
}

const getIntervalKey = (interval, index) =>
  String(interval?.interval ?? interval?.value ?? interval?.cluster ?? interval ?? index + 1)

const buildEvolutionSeriesFromRows = ({rowsByDate, intervalSource}) => {
  const intervals = Array.isArray(intervalSource) ? intervalSource : []
  const intervalKeys = intervals.map(getIntervalKey)
  const labels = getClassRanges(intervals)

  return intervalKeys.map((key, index) => ({
    color: CLASS_COLORS[index % CLASS_COLORS.length],
    name: labels[index] || `Clase ${index + 1}`,
    points: rowsByDate.map((rows) => {
      const intervalRow = (rows || []).find((row, rowIndex) => getIntervalKey(row, rowIndex) === key)
      const row = intervalRow || rows?.[index]
      return Number(row?.ha ?? row?.value ?? 0)
    }),
  }))
}

const buildAggregateDistributionEvolution = (aggregateDates) => {
  const validDates = aggregateDates.filter((dateData) => Array.isArray(dateData.rows) && dateData.rows.length > 0)

  if (validDates.length < 1) {
    return null
  }

  return {
    dates: validDates.map((dateData) => dateData.dateKey),
    series: validDates[0].rows.map((row, index) => ({
      color: row.color || CLASS_COLORS[index % CLASS_COLORS.length],
      name: row.label || `Clase ${index + 1}`,
      points: validDates.map((dateData) => Number(dateData.rows[index]?.value || 0)),
    })),
  }
}

const buildLandDistributionEvolutionFromAggregates = ({lands, aggregateDateData}) => {
  const validDates = aggregateDateData
    .filter((dateData) => Array.isArray(dateData?.intervals) && dateData.intervals.length > 0)
    .sort((left, right) => (parseReportDate(left.day) || 0) - (parseReportDate(right.day) || 0))

  if (validDates.length < 1) {
    return []
  }

  const intervalSource = validDates[0].intervals

  return lands
    .map((land) => {
      const landId = String(getLandId(land))
      const rowsByDate = validDates.map((dateData) => {
        const landDistribution = (dateData?.lands || []).find((item) => String(item.land_id) === landId)
        return Array.isArray(landDistribution?.interval) ? landDistribution.interval : []
      })
      const series = buildEvolutionSeriesFromRows({intervalSource, rowsByDate})

      if (!series.some((item) => item.points.some((value) => Number(value || 0) > 0))) {
        return null
      }

      return {
        dates: validDates.map((dateData) => getReportDateKey(dateData.day)),
        land,
        name: getLandName(land),
        series,
      }
    })
    .filter(Boolean)
}

const buildPdfData = (report = {}) => {
  const lands = Array.isArray(report.lands) ? report.lands : []
  const ndviDates = Array.isArray(report.ndvi_land_dates) ? report.ndvi_land_dates : []

  if (lands.length < 1) {
    return createEmptyPdfData()
  }

  const landsById = new Map(lands.map((land) => [String(getLandId(land)), land]))
  const reportsByLand = new Map()
  const usedDateKeys = new Set()

  ndviDates.forEach((item) => {
    const landId = String(item.land_id)
    const list = reportsByLand.get(landId) || []
    list.push(item)
    reportsByLand.set(landId, list)

    if (item.usage === 'USED') {
      const key = getReportDateKey(item.date)
      if (key) {
        usedDateKeys.add(key)
      }
    }
  })

  reportsByLand.forEach((items) => {
    items.sort((left, right) => (parseReportDate(left.date) || 0) - (parseReportDate(right.date) || 0))
  })

  const historyDates = Array.from(usedDateKeys).sort()
  const historySeries = lands
    .map((land, index) => {
      const usedReports = (reportsByLand.get(String(getLandId(land))) || []).filter((item) => item.usage === 'USED')
      const points = historyDates
        .map((dateKey) => {
          const closest = getClosestUsedReport(usedReports, parseReportDate(dateKey))
          return closest ? Number(closest.avg_index || 0) : null
        })
        .filter((value) => value !== null)

      return {
        color: LAND_COLORS[index % LAND_COLORS.length],
        land,
        name: getLandName(land),
        points,
      }
    })
    .filter((serie) => serie.points.length > 0)

  const aggregateDateData = Array.isArray(report.ndvi_aggregated_date) ? report.ndvi_aggregated_date : []
  const aggregateDates = aggregateDateData
    .map((dateData) => buildDistributionFromAggregate(dateData, landsById))
    .sort((left, right) => (parseReportDate(left.dateKey) || 0) - (parseReportDate(right.dateKey) || 0))

  const usedDateObjects = historyDates.map(parseReportDate).filter(Boolean).sort((left, right) => left - right)
  const rankingInitialDate = usedDateObjects[0] || null
  const rankingFinalDate = usedDateObjects[usedDateObjects.length - 1] || null

  return {
    aggregateDates,
    aggregateDistributionEvolution: buildAggregateDistributionEvolution(aggregateDates),
    fieldsCount: new Set(lands.map((land) => land?.field?.field_id || land?.field?.id || land?.field?.name).filter(Boolean)).size,
    historyDates,
    historySeries,
    includedLands: lands.map((land) => ({
      land,
      name: getLandName(land),
      surface: Number(land.ha || land.sup_ha || 0),
    })),
    landDistributionEvolution: buildLandDistributionEvolutionFromAggregates({lands, aggregateDateData}),
    lands,
    ranking: buildRanking({lands, reportsByLand, firstDate: rankingInitialDate, secondDate: rankingFinalDate}),
    rankingFinalDate,
    rankingInitialDate,
    totalHa: lands.reduce((sum, land) => sum + Number(land.ha || land.sup_ha || 0), 0),
  }
}

const buildPdf = ({brandLogo = null, labels, report}) => {
  const palette = getPdfPalette()
  const data = buildPdfData(report)
  const pages = [[]]
  const imageRegistry = []
  let y = 48

  const commands = () => pages[pages.length - 1]
  const newPage = () => {
    pages.push([])
    y = 48
  }
  const ensureSpace = (height) => {
    if (y + height > PAGE_HEIGHT - MARGIN_BOTTOM) {
      newPage()
    }
  }

  const painter = createPainter({
    push: (op) => commands().push(op),
    palette,
    pageWidth: PAGE_WIDTH,
    pageHeight: PAGE_HEIGHT,
  })

  const rawLine = (x1, y1, x2, y2, color = palette.line, width = 1) => {
    commands().push(`q ${rgbOps(color)} RG ${width} w ${x1} ${PAGE_HEIGHT - y1} m ${x2} ${PAGE_HEIGHT - y2} l S Q`)
  }

  const rawPolyline = (points, color, width = 1.5) => {
    if (!Array.isArray(points) || points.length < 2) {
      return
    }

    const path = points
      .map((point, index) => `${point.x.toFixed(2)} ${((PAGE_HEIGHT - point.y)).toFixed(2)} ${index === 0 ? 'm' : 'l'}`)
      .join(' ')
    commands().push(`q ${rgbOps(color)} RG ${width} w ${path} S Q`)
  }

  const rawSmoothPolyline = (points, color, width = 1.5) => {
    if (!Array.isArray(points) || points.length < 3) {
      rawPolyline(points, color, width)
      return
    }

    const path = [`${points[0].x.toFixed(2)} ${((PAGE_HEIGHT - points[0].y)).toFixed(2)} m`]

    for (let index = 1; index < points.length; index += 1) {
      const beforePrevious = points[index - 2] || points[index - 1]
      const previous = points[index - 1]
      const current = points[index]
      const next = points[index + 1] || current
      const minX = Math.min(previous.x, current.x)
      const maxX = Math.max(previous.x, current.x)
      const minY = Math.min(previous.y, current.y)
      const maxY = Math.max(previous.y, current.y)
      const cp1 = {
        x: clampNumber(previous.x + (current.x - beforePrevious.x) / 6, minX, maxX),
        y: clampNumber(previous.y + (current.y - beforePrevious.y) / 6, minY, maxY),
      }
      const cp2 = {
        x: clampNumber(current.x - (next.x - previous.x) / 6, minX, maxX),
        y: clampNumber(current.y - (next.y - previous.y) / 6, minY, maxY),
      }

      path.push(
        `${cp1.x.toFixed(2)} ${((PAGE_HEIGHT - cp1.y)).toFixed(2)} ` +
        `${cp2.x.toFixed(2)} ${((PAGE_HEIGHT - cp2.y)).toFixed(2)} ` +
        `${current.x.toFixed(2)} ${((PAGE_HEIGHT - current.y)).toFixed(2)} c`
      )
    }

    commands().push(`q ${rgbOps(color)} RG ${width} w 1 J 1 j ${path.join(' ')} S Q`)
  }

  const rawRotatedText = (value, x, baselineTop, angle, size = 7.2, color = palette.muted) => {
    const radians = (angle * Math.PI) / 180
    const cos = Math.cos(radians).toFixed(4)
    const sin = Math.sin(radians).toFixed(4)

    commands().push(
      `BT /F1 ${size} Tf ${rgbOps(color)} rg ${cos} ${sin} ${(-sin).toFixed(4)} ${cos} ` +
      `${x.toFixed(2)} ${(PAGE_HEIGHT - baselineTop).toFixed(2)} Tm <${escapePdfText(value)}> Tj ET`
    )
  }

  const registerImage = (image) => {
    if (!image?.dataUrl || !image.width || !image.height) {
      return null
    }
    const hex = getDataUrlImageHex(image.dataUrl)
    if (!hex) {
      return null
    }
    const name = `Im${imageRegistry.length + 1}`
    imageRegistry.push({...image, hex, name})
    return name
  }

  const paragraph = (value, options = {}) => {
    const x = options.x || MARGIN_X
    const width = options.width || CONTENT_WIDTH
    const size = options.size || 9.5
    const lineHeight = options.lineHeight || Math.round(size * 1.45)
    const lines = wrapText(value, width, size, options.bold)

    ensureSpace(lines.length * lineHeight + 4)
    lines.forEach((lineText) => {
      painter.text(lineText, x, y, size, {
        bold: options.bold,
        color: options.color || palette.slate,
        maxWidth: width,
      })
      y += lineHeight
    })
  }

  const sectionTitle = (title, options = {}) => {
    if (options.forceNewPage && commands().length > 0) {
      newPage()
    }

    ensureSpace(options.reserve || 38)
    y = drawSectionHeader(painter, y, title)
  }

  const drawLegend = (items, top, x = MARGIN_X) => {
    let cursorX = x
    let cursorY = top
    const lineHeight = 16

    items.forEach((item) => {
      const label = String(item.label || '')
      const width = Math.min(150, estimateTextWidth(label, 8.5, true) + 18)

      if (cursorX > x && cursorX + width > MARGIN_X + CONTENT_WIDTH) {
        cursorX = x
        cursorY += lineHeight
      }

      painter.roundedRect(cursorX, cursorY - 7, 8, 8, 2, {fill: item.color})
      painter.text(label, cursorX + 12, cursorY, 8.5, {bold: true, color: palette.muted, maxWidth: width - 12})
      cursorX += width + 10
    })

    return cursorY - top + lineHeight
  }

  const drawDatePills = (items) => {
    const metrics = items.map(({label, value}) => {
      const labelText = String(label || '')
      const valueText = formatReportDate(value)
      const width = Math.max(
        148,
        Math.min(190, estimateTextWidth(labelText, 7.5, true) + estimateTextWidth(valueText, 13, true) + 42)
      )

      return {
        label: labelText,
        value: valueText,
        width,
      }
    })

    ensureSpace(76)
    y = drawMetricChips(painter, y, metrics)
  }

  const drawLineChart = ({dates, series, title, valueFormatter = (value) => formatFixedNumber(value, 2)}) => {
    if (!Array.isArray(dates) || dates.length < 1 || !Array.isArray(series) || series.length < 1) {
      paragraph(labels.noData, {color: palette.muted})
      return
    }

    ensureSpace(CHART_HEIGHT + 74)
    painter.text(title, MARGIN_X, y, 10.5, {bold: true, color: palette.ink})
    y += 16

    const chartX = MARGIN_X + 8
    const chartY = y
    const chartW = CONTENT_WIDTH - 16
    const chartH = CHART_HEIGHT
    const allValues = series.flatMap((item) => item.points).filter((value) => Number.isFinite(value))
    const dateTimes = dates.map((date) => parseReportDate(date)?.getTime())
    const validDateTimes = dateTimes.filter((time) => Number.isFinite(time))
    const minDateTime = validDateTimes.length > 0 ? Math.min(...validDateTimes) : 0
    const maxDateTime = validDateTimes.length > 0 ? Math.max(...validDateTimes) : 0
    const dateTimeRange = maxDateTime - minDateTime
    const minValue = Math.min(0, ...allValues)
    const maxValue = Math.max(1, ...allValues)
    const valueRange = maxValue - minValue || 1
    const maxValueLabel = valueFormatter(maxValue)
    const minValueLabel = valueFormatter(minValue)
    const yAxisLabelWidth = Math.max(
      32,
      estimateTextWidth(maxValueLabel, 7.5) + 4,
      estimateTextWidth(minValueLabel, 7.5) + 4
    )
    const leftPad = Math.max(36, Math.ceil(yAxisLabelWidth + 8))
    const rightPad = 44
    const topPad = 16
    const bottomPad = 44
    const plotX = chartX + leftPad
    const plotY = chartY + topPad
    const plotW = chartW - leftPad - rightPad
    const plotH = chartH - topPad - bottomPad
    const getPointX = (index) => {
      const dateTime = dateTimes[index]

      if (!Number.isFinite(dateTime) || dateTimeRange <= 0) {
        return plotX + (dates.length === 1 ? plotW / 2 : (plotW * index) / (dates.length - 1))
      }

      return plotX + ((dateTime - minDateTime) / dateTimeRange) * plotW
    }

    painter.roundedRect(chartX, chartY, chartW, chartH, 12, {fill: palette.surface, stroke: palette.line, lineWidth: 1})
    rawLine(plotX, plotY, plotX, plotY + plotH, palette.line, 0.8)
    rawLine(plotX, plotY + plotH, plotX + plotW, plotY + plotH, palette.line, 0.8)
    painter.text(maxValueLabel, chartX + 6, plotY + 4, 7.5, {color: palette.muted, maxWidth: yAxisLabelWidth})
    painter.text(minValueLabel, chartX + 6, plotY + plotH, 7.5, {color: palette.muted, maxWidth: yAxisLabelWidth})

    series.slice(0, 10).forEach((item) => {
      const points = item.points.map((value, index) => ({
        x: getPointX(index),
        y: plotY + plotH - ((value - minValue) / valueRange) * plotH,
      }))
      rawSmoothPolyline(points, item.color, 1.8)
    })

    dates.forEach((_, dateIndex) => {
      const tickX = getPointX(dateIndex)
      rawLine(tickX, plotY + plotH, tickX, plotY + plotH + 3, palette.line, 0.5)
    })

    getChartDateLabelItems({dates, getPointX, maxLabelX: chartX + chartW - 8, plotX, plotW}).forEach(({dateLabel, labelX}) => {
      rawRotatedText(dateLabel, labelX, plotY + plotH + 8, -45, DATE_AXIS_LABEL_SIZE, palette.muted)
    })

    y += chartH + 12
    y += drawLegend(series.slice(0, 6).map((item) => ({color: item.color, label: item.name})), y, plotX) + 8
  }

  const getTableHeight = ({rows, title}) =>
    (title ? TABLE_TITLE_HEIGHT : 0) + TABLE_HEADER_HEIGHT + (rows?.length || 0) * ROW_HEIGHT + TABLE_BOTTOM_PADDING

  const canKeepTogether = (height) => height <= PAGE_HEIGHT - 48 - MARGIN_BOTTOM

  const drawTable = ({columns, rows, title, keepTogether = false}) => {
    if (!Array.isArray(rows) || rows.length < 1) {
      return
    }

    const tableHeight = getTableHeight({rows, title})
    if (keepTogether && canKeepTogether(tableHeight)) {
      ensureSpace(tableHeight)
    }

    const drawHeader = () => {
      painter.roundedRect(MARGIN_X, y, CONTENT_WIDTH, TABLE_HEADER_HEIGHT, 7, {fill: palette.navy})
      let cursorX = MARGIN_X + 10
      columns.forEach((column) => {
        painter.text(column.label, cursorX, y + 14, column.headerSize || 8, {
          bold: true,
          color: palette.onNavy,
          maxWidth: column.width - 8,
        })
        cursorX += column.width
      })
      y += TABLE_HEADER_HEIGHT
    }

    if (title) {
      ensureSpace(TABLE_TITLE_HEIGHT + TABLE_HEADER_HEIGHT + ROW_HEIGHT + TABLE_BOTTOM_PADDING)
      painter.text(title, MARGIN_X, y, 10.5, {bold: true, color: palette.ink})
      y += TABLE_TITLE_HEIGHT
    }

    ensureSpace(TABLE_HEADER_HEIGHT + ROW_HEIGHT + TABLE_BOTTOM_PADDING)
    drawHeader()
    rows.forEach((row) => {
      if (y + ROW_HEIGHT + TABLE_BOTTOM_PADDING > PAGE_HEIGHT - MARGIN_BOTTOM) {
        newPage()
        drawHeader()
      }

      painter.fillRect(MARGIN_X, y, CONTENT_WIDTH, ROW_HEIGHT, row.background || palette.surface)
      painter.hline(MARGIN_X, y + ROW_HEIGHT, PAGE_WIDTH - MARGIN_X, palette.lineSoft, 0.6)
      let cursorX = MARGIN_X + 10
      columns.forEach((column) => {
        const value = column.get(row)
        painter.text(value, cursorX, y + 12, 8.5, {
          bold: column.bold,
          color: column.color ? column.color(row) : palette.slate,
          maxWidth: column.width - 8,
        })
        cursorX += column.width
      })
      y += ROW_HEIGHT
    })
    y += TABLE_BOTTOM_PADDING
  }

  const logoName = registerImage(brandLogo)
  const drawLogo = logoName
    ? (currentPainter, {x, top, maxWidth, maxHeight}) => {
        const ratio = Math.min(maxWidth / brandLogo.width, maxHeight / brandLogo.height, 1)
        const width = Math.round(brandLogo.width * ratio)
        const height = Math.round(brandLogo.height * ratio)
        currentPainter.image(logoName, x, top + Math.round((maxHeight - height) / 2), width, height)
      }
    : null

  y = drawHeaderBand(painter, {
    drawLogo,
    eyebrow: labels.eyebrow,
    metaLabel: labels.generatedAt,
    metaValue: formatReportDate(new Date()),
    title: report?.name || labels.title,
  })

  y = drawMetricChips(painter, y, [
    {label: labels.period, value: `${formatReportDate(report?.from_date)} - ${formatReportDate(report?.to_date)}`, width: 190, valueSize: 12},
    {label: labels.surface, value: formatHectares(data.totalHa), width: 130},
    {label: labels.fields, value: data.fieldsCount || '-', width: 78},
    {label: labels.lands, value: data.lands.length, width: 78},
  ])

  const sourceValue = getNdviReportSourceLabel(report)
  if (sourceValue) {
    painter.text(`${labels.source}: ${sourceValue}`, MARGIN_X, y, 9, {color: palette.muted})
    y += 18
  }

  sectionTitle(labels.includedLandsTitle, {
    reserve: 38 + getTableHeight({rows: data.includedLands, title: null}),
  })
  drawTable({
    columns: [
      {label: labels.land, width: 360, get: (row) => row.name, bold: true},
      {label: labels.surface, width: 135, get: (row) => formatHectares(row.surface)},
    ],
    rows: data.includedLands,
    keepTogether: true,
  })

  sectionTitle(labels.historyTitle, {forceNewPage: true, reserve: CHART_HEIGHT + 100})
  drawLineChart({
    dates: data.historyDates,
    series: data.historySeries,
    title: labels.ndviAverage,
  })

  sectionTitle(labels.distributionEvolutionTitle, {forceNewPage: true, reserve: CHART_HEIGHT + 100})
  data.landDistributionEvolution.forEach((evolution) => {
    ensureSpace(CHART_HEIGHT + 92)
    drawLineChart({
      dates: evolution.dates,
      series: evolution.series,
      title: evolution.name,
      valueFormatter: (value) => formatHectares(value, 1),
    })
  })

  if (data.aggregateDistributionEvolution) {
    ensureSpace(CHART_HEIGHT + 92)
    drawLineChart({
      dates: data.aggregateDistributionEvolution.dates,
      series: data.aggregateDistributionEvolution.series,
      title: labels.aggregateDistributionTitle,
      valueFormatter: (value) => formatHectares(value, 1),
    })
  } else {
    paragraph(labels.noData, {color: palette.muted})
  }

  sectionTitle(labels.rankingTitle, {forceNewPage: true})
  drawDatePills([
    {label: labels.initialDate, value: data.rankingInitialDate},
    {label: labels.finalDate, value: data.rankingFinalDate},
  ])
  drawTable({
    columns: [
      {label: labels.land, width: 103, get: (row) => getLandName(row.land), bold: true, headerSize: 7.2},
      {label: labels.initialPosition, width: 78, get: (row) => String(row.initialPosition), headerSize: 7.2},
      {label: labels.ndviInitialDate, width: 92, get: (row) => formatFixedNumber(row.firstAvgIndex, 3), headerSize: 7.2},
      {label: labels.finalPosition, width: 74, get: (row) => String(row.finalPosition), headerSize: 7.2},
      {label: labels.ndviFinalDate, width: 92, get: (row) => formatFixedNumber(row.secondAvgIndex, 3), headerSize: 7.2},
      {label: labels.rankingVariation, width: 56, get: (row) => formatRankingVariation(row.variation), headerSize: 7.2},
    ],
    rows: data.ranking,
  })

  pages.forEach((pageCommands, index) => {
    const pagePainter = createPainter({
      push: (op) => pageCommands.push(op),
      palette,
      pageWidth: PAGE_WIDTH,
      pageHeight: PAGE_HEIGHT,
    })
    drawFooter(pagePainter, {index, total: pages.length})
  })

  const objects = []
  const setObject = (id, body) => {
    objects[id] = body
  }
  const nextId = () => objects.length

  setObject(1, '<< /Type /Catalog /Pages 2 0 R >>')
  setObject(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>')
  setObject(4, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>')

  imageRegistry.forEach((image) => {
    if (!image.hex) {
      return
    }

    const imageId = nextId()
    image.objectId = imageId
    setObject(
      imageId,
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${image.hex.length + 1} >>\nstream\n${image.hex}>\nendstream`
    )
  })

  const imageResource = imageRegistry
    .filter((image) => image.objectId)
    .map((image) => `/${image.name} ${image.objectId} 0 R`)
    .join(' ')
  const xObjectResource = imageResource ? `/XObject << ${imageResource} >> ` : ''
  const pageIds = []

  pages.forEach((pageCommands) => {
    const contentStream = pageCommands.join('\n')
    const contentId = nextId()
    setObject(contentId, `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`)
    const pageId = nextId()
    setObject(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> ${xObjectResource}>> /Contents ${contentId} 0 R >>`
    )
    pageIds.push(pageId)
  })

  setObject(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`
  }

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length}\n`
  pdf += '0000000000 65535 f \n'
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return pdf
}

export const buildNdviReportPdfString = ({brandLogo = null, labels, report}) =>
  buildPdf({brandLogo, labels, report})

export const buildNdviReportPdfBlob = async ({labels, report}) => {
  const palette = getPdfPalette()
  const brandLogo = await loadTenantLogoImage(palette.navy)
  return new Blob([buildNdviReportPdfString({brandLogo, labels, report})], {type: 'application/pdf'})
}

export const downloadNdviReportPdf = async ({labels, report}) => {
  const blob = await buildNdviReportPdfBlob({labels, report})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = createSafeFileName(report?.name, labels.title)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
